import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, UserPlus, Clock, ThumbsUp, Loader2, Coins } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AvatarWithHover = ({ user, showOnline = false }) => {
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Link to={`/profile/${user.id}`} className="relative group cursor-pointer">
                        <div className="nk-avatar w-10 h-10">
                            <img 
                                src={user.avatar} 
                                alt={user.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {showOnline && (
                            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-accent-green rounded-full border-2 border-card" />
                        )}
                    </Link>
                </TooltipTrigger>
                <TooltipContent side="top" className="nk-box p-2">
                    <p className="font-medium text-sm">{user.name}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export const LeftSidebar = () => {
    const { user, isAuthenticated } = useAuth();
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [newUsers, setNewUsers] = useState([]);
    const [profileViewers, setProfileViewers] = useState([]);
    const [totalViews, setTotalViews] = useState(0);
    const [loading, setLoading] = useState(true);
    const [thumbsUpGiven, setThumbsUpGiven] = useState({});

    useEffect(() => {
        fetchData();
        
        // Send heartbeat every 2 minutes if logged in
        if (isAuthenticated) {
            const heartbeat = setInterval(() => {
                fetch(`${API_URL}/api/homepage/heartbeat`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }).catch(() => {});
            }, 120000);
            return () => clearInterval(heartbeat);
        }
    }, [isAuthenticated]);

    const fetchData = async () => {
        try {
            // Fetch online users
            const onlineRes = await fetch(`${API_URL}/api/homepage/online-users?limit=8`);
            if (onlineRes.ok) {
                const data = await onlineRes.json();
                setOnlineUsers(data.users || []);
            }

            // Fetch new users
            const newRes = await fetch(`${API_URL}/api/homepage/new-users?limit=5`);
            if (newRes.ok) {
                const data = await newRes.json();
                setNewUsers(data.users || []);
            }

            // Fetch profile viewers if logged in
            if (isAuthenticated) {
                const token = localStorage.getItem('token');
                const viewersRes = await fetch(`${API_URL}/api/homepage/profile-viewers?limit=5`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (viewersRes.ok) {
                    const data = await viewersRes.json();
                    setProfileViewers(data.viewers || []);
                    setTotalViews(data.total || 0);
                }
            }
        } catch (error) {
            console.error('Error fetching sidebar data:', error);
        }
        setLoading(false);
    };

    const handleThumbsUp = (userId) => {
        setThumbsUpGiven(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }));
    };

    const defaultAvatar = (name) => `https://api.dicebear.com/7.x/avataaars/svg?seed=${name || 'guest'}`;

    return (
        <aside className="space-y-4">
            {/* My Profile Summary - Only when logged in */}
            {isAuthenticated && user && (
                <div className="nk-box overflow-hidden">
                    <div className="nk-box-header flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Mój profil
                    </div>
                    <div className="p-3">
                        <Link to="/profile" className="flex items-center gap-3 mb-3 hover:opacity-80 transition-opacity">
                            <div className="nk-avatar w-14 h-14">
                                <img 
                                    src={user.avatar_url || defaultAvatar(user.nickname)} 
                                    alt={user.display_name || user.nickname} 
                                    className="w-full h-full object-cover" 
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-sm text-foreground truncate">
                                    {user.display_name || user.nickname}
                                </h3>
                                <p className="text-xs text-muted-foreground truncate">
                                    {user.schools?.[0]?.name || 'Dodaj swoją szkołę'}
                                </p>
                            </div>
                        </Link>
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-muted rounded-lg p-2">
                                <div className="font-bold text-primary text-sm">{user.profile_views || 0}</div>
                                <div className="text-[10px] text-muted-foreground">Wyświetlenia</div>
                            </div>
                            <div className="bg-muted rounded-lg p-2">
                                <div className="font-bold text-accent-green text-sm">{user.friends_count || 0}</div>
                                <div className="text-[10px] text-muted-foreground">Znajomi</div>
                            </div>
                            <Link to="/zorki" className="bg-muted rounded-lg p-2 hover:bg-amber-500/20 transition-colors">
                                <div className="font-bold text-amber-500 text-sm flex items-center justify-center gap-1">
                                    <Coins className="w-3 h-3" />
                                    {user.zorki_balance || 0}
                                </div>
                                <div className="text-[10px] text-muted-foreground">Zorki</div>
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Who Viewed My Profile - Only when logged in */}
            {isAuthenticated && (
                <div className="nk-box overflow-hidden">
                    <div className="nk-box-header flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Kto mnie ostatnio oglądał
                    </div>
                    <div className="p-3 space-y-2">
                        {loading ? (
                            <div className="flex justify-center py-4">
                                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                            </div>
                        ) : profileViewers.length > 0 ? (
                            <>
                                {profileViewers.map((viewer) => (
                                    <Link 
                                        key={viewer.id} 
                                        to={`/profile/${viewer.id}`}
                                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors group"
                                    >
                                        <div className="nk-avatar w-9 h-9">
                                            <img src={viewer.avatar} alt={viewer.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-foreground truncate">{viewer.name}</p>
                                            <p className="text-[10px] text-muted-foreground">{viewer.viewedAgo}</p>
                                        </div>
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                                onClick={(e) => { e.preventDefault(); handleThumbsUp(viewer.id); }}
                                                className={`p-1 rounded transition-colors ${thumbsUpGiven[viewer.id] ? 'text-accent-green' : 'text-muted-foreground hover:text-accent-green'}`}
                                            >
                                                <ThumbsUp className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </Link>
                                ))}
                                <Button variant="ghost" className="w-full text-xs text-primary hover:text-primary-light h-8">
                                    Zobacz wszystkich ({totalViews})
                                </Button>
                            </>
                        ) : (
                            <p className="text-xs text-muted-foreground text-center py-2">
                                Nikt jeszcze nie oglądał Twojego profilu
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Online Users */}
            <div className="nk-box overflow-hidden">
                <div className="nk-box-header flex items-center justify-between">
                    <span className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-accent-green rounded-full animate-nk-pulse" />
                        Ostatnio online
                    </span>
                    <Badge className="nk-badge text-[10px]">{onlineUsers.length}</Badge>
                </div>
                <div className="p-3">
                    {loading ? (
                        <div className="flex justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                    ) : onlineUsers.length > 0 ? (
                        <>
                            <div className="grid grid-cols-4 gap-2">
                                {onlineUsers.slice(0, 8).map((u) => (
                                    <AvatarWithHover key={u.id} user={u} showOnline />
                                ))}
                            </div>
                            <Button variant="ghost" className="w-full text-xs text-primary hover:text-primary-light mt-3 h-8">
                                Pokaż więcej
                            </Button>
                        </>
                    ) : (
                        <p className="text-xs text-muted-foreground text-center py-2">
                            Brak użytkowników online
                        </p>
                    )}
                </div>
            </div>

            {/* New Users */}
            <div className="nk-box overflow-hidden">
                <div className="nk-box-header flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Nowi użytkownicy
                </div>
                <div className="p-3 space-y-2">
                    {loading ? (
                        <div className="flex justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                    ) : newUsers.length > 0 ? (
                        newUsers.map((u) => (
                            <Link 
                                key={u.id} 
                                to={`/profile/${u.id}`}
                                className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors"
                            >
                                <div className="nk-avatar w-9 h-9">
                                    <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-foreground truncate">{u.name}</p>
                                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {u.joinedAgo}
                                    </p>
                                </div>
                                <Button 
                                    size="sm" 
                                    className="nk-button-green h-7 text-[10px] px-2"
                                    onClick={(e) => e.preventDefault()}
                                >
                                    Dodaj
                                </Button>
                            </Link>
                        ))
                    ) : (
                        <p className="text-xs text-muted-foreground text-center py-2">
                            Brak nowych użytkowników
                        </p>
                    )}
                </div>
            </div>
        </aside>
    );
};
